import { PrSelectOption } from '@app/components/pr-select/pr-select.component';
import { EMPTY, Observable, catchError, filter, startWith } from 'rxjs';
import {
  LegalEntitySummary,
  PledgeInitParams,
  PledgeLegalEntityChoices,
} from '../../definition/interface/Pledge';

export interface LegalEntitySelection {
  sellerLegalEntities: Record<number, number>;
  attachedLegalEntityId: number | null;
}

function toOptions(legalEntities: LegalEntitySummary[]): PrSelectOption[] {
  return (legalEntities || []).map(legalEntity => new PrSelectOption(legalEntity.name, legalEntity.id));
}

export function defaultLegalEntitySelection(choices: PledgeLegalEntityChoices): LegalEntitySelection {
  const sellerLegalEntities: Record<number, number> = {};
  choices.sellers
    .filter(seller => seller.defaultLegalEntityId != null)
    .forEach(seller => (sellerLegalEntities[seller.memberId] = seller.defaultLegalEntityId));
  return { sellerLegalEntities, attachedLegalEntityId: choices.defaultAttachedLegalEntityId ?? null };
}

export function sellerLegalEntityOptions(choices: PledgeLegalEntityChoices, memberId: number): PrSelectOption[] {
  return toOptions(choices?.sellers.find(seller => seller.memberId === memberId)?.legalEntities);
}

export function showsSellerLegalEntitySelect(choices: PledgeLegalEntityChoices, memberId: number): boolean {
  return sellerLegalEntityOptions(choices, memberId).length >= 2;
}

export function attachedLegalEntityOptions(choices: PledgeLegalEntityChoices): PrSelectOption[] {
  return toOptions(choices?.attachedLegalEntities);
}

export function attachedLegalEntityOwnerLabel(choices: PledgeLegalEntityChoices): string {
  return choices?.attachedToReferer ? 'Entité légale du référent' : 'Entité légale euodia';
}

/**
 * Le rattachement n'a son propre select que lorsqu'il appartient à un autre membre que le vendeur : sinon il suit l'entité de vente du conseiller
 * principal.
 */
export function showsAttachedLegalEntitySelect(choices: PledgeLegalEntityChoices): boolean {
  return !!choices?.attachedChosenSeparately && attachedLegalEntityOptions(choices).length >= 2;
}

/**
 * Quand le rattachement ne se choisit pas à part, l'entité de vente du conseiller principal est aussi celle de rattachement.
 */
export function selectSellerLegalEntity(
  choices: PledgeLegalEntityChoices,
  selection: LegalEntitySelection,
  memberId: number,
  legalEntityId: number,
): LegalEntitySelection {
  const followsSellerChoice =
    memberId === choices.advisorMemberId &&
    !choices.attachedChosenSeparately &&
    choices.attachedLegalEntities.some(legalEntity => legalEntity.id === legalEntityId);
  return {
    sellerLegalEntities: { ...selection.sellerLegalEntities, [memberId]: legalEntityId },
    attachedLegalEntityId: followsSellerChoice ? legalEntityId : selection.attachedLegalEntityId,
  };
}

/**
 * Charge les choix d'entités légales une fois par couple type de produit / produit, retente après une erreur et ignore la réponse d'une requête
 * dépassée par une plus récente. Chaque nouvelle requête émet d'abord `null` : les choix précédents ne valent plus, et restent abandonnés si la
 * requête échoue ; seul l'échec de la requête en cours est signalé à `onError`.
 */
export class LegalEntityChoicesLoader {
  private currentRequest: string = null;

  constructor(
    private fetch: (productType: string, productId?: number) => Observable<PledgeLegalEntityChoices>,
    private onError: () => void = () => {},
  ) {}

  load(productType: string, productId: number | undefined): Observable<PledgeLegalEntityChoices | null> {
    const request = `${productType}-${productId}`;
    if (!productType || request === this.currentRequest) {
      return EMPTY;
    }
    this.currentRequest = request;
    return this.fetch(productType, productId).pipe(
      catchError(() => {
        if (this.currentRequest === request) {
          this.currentRequest = null;
          this.onError();
        }
        return EMPTY;
      }),
      filter(() => request === this.currentRequest),
      startWith(null),
    );
  }
}

export function toLegalEntityInitParams(
  choices: PledgeLegalEntityChoices | null,
  selection: LegalEntitySelection | null,
  displayedSellerIds: number[],
): Pick<PledgeInitParams, 'sellerLegalEntities' | 'attachedLegalEntityId'> {
  if (!choices || !selection) {
    return {};
  }
  const sellerLegalEntities: Record<number, number> = {};
  displayedSellerIds
    .filter(memberId => selection.sellerLegalEntities[memberId] != null)
    .forEach(memberId => (sellerLegalEntities[memberId] = selection.sellerLegalEntities[memberId]));
  return { sellerLegalEntities, attachedLegalEntityId: selection.attachedLegalEntityId };
}
